type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  requestId?: string;
  [key: string]: unknown;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

type LogHandler = (entry: LogEntry) => void;

class Logger {
  private static instance: Logger;
  private handlers: LogHandler[] = [];
  private minLevel: LogLevel = 'debug';
  private buffer: LogEntry[] = [];
  private maxBufferSize = 100;

  private levelPriority: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  private constructor() {
    const env = import.meta.env.MODE;
    this.minLevel = env === 'production' ? 'warn' : 'debug';

    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        this.error('Uncaught error', {
          error: event.error,
          context: { filename: event.filename, lineno: event.lineno, colno: event.colno },
        });
      });

      window.addEventListener('unhandledrejection', (event) => {
        this.error('Unhandled promise rejection', {
          error: event.reason,
          context: { type: 'unhandledrejection' },
        });
      });
    }
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  setMinLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  addHandler(handler: LogHandler): void {
    this.handlers.push(handler);
  }

  removeHandler(handler: LogHandler): void {
    this.handlers = this.handlers.filter(h => h !== handler);
  }

  getBuffer(): LogEntry[] {
    return [...this.buffer];
  }

  clearBuffer(): void {
    this.buffer = [];
  }

  private shouldLog(level: LogLevel): boolean {
    return this.levelPriority[level] >= this.levelPriority[this.minLevel];
  }

  private formatError(error: unknown): LogEntry['error'] | undefined {
    if (!error) return undefined;

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    return {
      name: 'Unknown',
      message: String(error),
    };
  }

  private log(level: LogLevel, message: string, options?: { context?: LogContext; error?: unknown }): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: options?.context,
      error: this.formatError(options?.error),
    };

    this.buffer.push(entry);
    if (this.buffer.length > this.maxBufferSize) {
      this.buffer.shift();
    }

    this.handlers.forEach(handler => {
      try {
        handler(entry);
      } catch (e) {
        console.error('Log handler error:', e);
      }
    });

    const contextStr = options?.context ? ` ${JSON.stringify(options.context)}` : '';
    const errorStr = entry.error ? `\n${entry.error.stack || entry.error.message}` : '';

    switch (level) {
      case 'debug':
        console.debug(`[${entry.timestamp}] DEBUG: ${message}${contextStr}${errorStr}`);
        break;
      case 'info':
        console.info(`[${entry.timestamp}] INFO: ${message}${contextStr}${errorStr}`);
        break;
      case 'warn':
        console.warn(`[${entry.timestamp}] WARN: ${message}${contextStr}${errorStr}`);
        break;
      case 'error':
        console.error(`[${entry.timestamp}] ERROR: ${message}${contextStr}${errorStr}`);
        break;
    }
  }

  debug(message: string, context?: LogContext): void {
    this.log('debug', message, { context });
  }

  info(message: string, context?: LogContext): void {
    this.log('info', message, { context });
  }

  warn(message: string, context?: LogContext): void {
    this.log('warn', message, { context });
  }

  error(message: string, options?: { error?: unknown; context?: LogContext }): void {
    this.log('error', message, options);
  }

  api(method: string, url: string, status: number, duration: number, error?: unknown): void {
    const level: LogLevel = status >= 500 ? 'error' : status >= 400 ? 'warn' : 'debug';
    this.log(level, `${method} ${url} ${status} (${duration}ms)`, {
      context: { action: 'api', method, url, status, duration },
      error,
    });
  }

  component(name: string, action: string, context?: LogContext): void {
    this.debug(`[${name}] ${action}`, { ...context, component: name, action });
  }
}

export const logger = Logger.getInstance();

export function createComponentLogger(componentName: string) {
  return {
    debug: (message: string, context?: LogContext) =>
      logger.debug(`[${componentName}] ${message}`, { ...context, component: componentName }),
    info: (message: string, context?: LogContext) =>
      logger.info(`[${componentName}] ${message}`, { ...context, component: componentName }),
    warn: (message: string, context?: LogContext) =>
      logger.warn(`[${componentName}] ${message}`, { ...context, component: componentName }),
    error: (message: string, error?: unknown, context?: LogContext) =>
      logger.error(`[${componentName}] ${message}`, { error, context: { ...context, component: componentName } }),
  };
}
